import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useField } from "formik";
import PhoneInput, { getCountryCallingCode } from "react-phone-number-input";
import InputFormik from "app/pages/.shared/form/InputFormik";
import { FormattedMessage } from "react-intl";
import { datadogRum } from "@datadog/browser-rum";
import "./PhoneIntl.scss";
import PhoneFlag from "app/pages/.shared/profile/PhoneFlag/PhoneFlag";
import appGlobalsContext from "app/AppGlobalsContext";
import classNames from "classnames";

const COUNTRY_LABEL_KEY = /^[A-Z]{2}$/;

/**
 * Appends calling codes to country names for the country `<select/>` options.
 * @param {Record<string, string> | undefined} labels
 * @returns {Record<string, string> | undefined}
 */
const labelsWithCountryCallingCodes = labels => {
	if (!labels) {
		return labels;
	}
	const next = { ...labels };
	for (const key of Object.keys(next)) {
		if (!COUNTRY_LABEL_KEY.test(key)) {
			continue;
		}
		try {
			const callingCode = getCountryCallingCode(key);
			next[key] = `${next[key]} (+${callingCode})`;
		} catch {
			// Country not in bundled metadata; keep original label.
		}
	}
	return next;
};

const PhoneIntl = ({
	id,
	name,
	isRequired,
	shop,
	setFieldValue,
	setFieldTouched,
	setFieldError,
	label,
	...restProps
}) => {
	const { countrySelectProps: userCountrySelectProps, ...phoneInputRestProps } = restProps;
	const [field] = useField(name);
	const [lang, country] = shop.split("-");
	const [locale, setLocale] = useState();
	const [hasChanged, setHasChanged] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [isNumberFocused, setIsNumberFocused] = useState(false);
	const [phoneInputRenderKey, setPhoneInputRenderKey] = useState(0);
	const [selectedCountry, setSelectedCountry] = useState(country);

	const { isRTL } = useContext(appGlobalsContext);

	const handleFocus = () => {
		setIsFocused(true);
		setIsNumberFocused(true);
	};

	const handleBlur = () => {
		setIsFocused(false);
		setIsNumberFocused(false);
		setHasChanged(false);
		setPhoneInputRenderKey(renderKey => renderKey + 1);
		if (typeof phoneInputRestProps.onBlur === "function") {
			phoneInputRestProps.onBlur();
		}
	};

	const handleChange = useCallback(
		value => {
			setFieldTouched(name);
			setFieldValue(name, value);
			if (value !== "" && !hasChanged) {
				setFieldError(name);
				setHasChanged(true);
			}
		},
		[name, hasChanged]
	);

	useEffect(() => {
		import(/* webpackChunkName: "phone-country-[request]" */
		`/node_modules/react-phone-number-input/locale/${lang}.json`)
			.then(locale => {
				setLocale(locale.default);
			})
			.catch(err => {
				datadogRum.addError(err);
			});
	}, [lang]);

	const handleCountryChange = useCallback(newCountry => {
		if (newCountry) {
			setSelectedCountry(newCountry);
		}
	}, []);

	const countryPrefix = useMemo(() => {
		try {
			return `+${getCountryCallingCode(selectedCountry)}`;
		} catch {
			return null;
		}
	}, [selectedCountry]);

	const hasRealValue = field?.value && field.value !== countryPrefix;

	const labels = useMemo(() => (isFocused ? labelsWithCountryCallingCodes(locale) : locale), [
		locale,
		isFocused,
	]);

	const countrySelectProps = useMemo(
		() => ({
			...userCountrySelectProps,
			onFocus: event => {
				if (typeof userCountrySelectProps?.onFocus === "function") {
					userCountrySelectProps.onFocus(event);
				}
				setIsFocused(true);
			},
			onBlur: event => {
				if (typeof userCountrySelectProps?.onBlur === "function") {
					userCountrySelectProps.onBlur(event);
				}
				setIsFocused(false);
			},
		}),
		[userCountrySelectProps]
	);

	const numberInputProps = useMemo(
		() => ({
			type: "tel",
			label: label,
			maxLength: "50",
			dir: "ltr",
			style: { textAlign: isRTL ? "right" : "left" },
			labelFloatOverride: ({ meta }) =>
				isFocused || hasRealValue || Boolean(meta.touched && meta.error),
		}),
		[label, isRTL, isFocused, hasRealValue]
	);

	return (
		<PhoneInput
			key={phoneInputRenderKey}
			{...phoneInputRestProps}
			id={id}
			name={name}
			className={classNames("phone-intl-input", {
				"phone-intl-input--has-value": hasRealValue,
				"phone-intl-input--only-prefix": !hasRealValue && !isFocused,
			})}
			international={isNumberFocused ? undefined : true}
			// Keep the selected country when users erase the calling code from the input.
			addInternationalOption={false}
			inputComponent={InputFormik}
			defaultCountry={selectedCountry}
			value={field.value}
			labels={labels}
			flagComponent={PhoneFlag}
			shouldSanitize={false}
			onChange={handleChange}
			isRequired={isRequired}
			numberInputProps={numberInputProps}
			countrySelectProps={countrySelectProps}
			onCountryChange={handleCountryChange}
			onFocus={handleFocus}
			onBlur={handleBlur}
		/>
	);
};

PhoneIntl.defaultProps = {
	label: <FormattedMessage id="phone.label" />,
};

PhoneIntl.propTypes = {
	id: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired,
	isRequired: PropTypes.bool,
	shop: PropTypes.string,
	setFieldValue: PropTypes.func,
	setFieldTouched: PropTypes.func,
	setFieldError: PropTypes.func,
	label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
};

export default PhoneIntl;
